import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const LaserHairRemoval = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const benefits = [
    {
      title: 'Long-Lasting Results',
      description: 'Provides permanent hair reduction with results that last significantly longer than shaving or waxing.'
    },
    {
      title: 'Precision Targeting',
      description: 'Targets specific areas with precision while leaving surrounding skin undamaged.'
    },
    {
      title: 'Saves Time and Money',
      description: 'Eliminates the need for frequent shaving or waxing appointments, saving time and money long-term.'
    },
    {
      title: 'Minimal Discomfort',
      description: 'Modern laser technology minimizes discomfort during treatment with advanced cooling systems.'
    },
    {
      title: 'Reduces Ingrown Hairs',
      description: 'Significantly reduces ingrown hairs and skin irritation commonly caused by shaving.'
    },
    {
      title: 'Works on Various Skin Tones',
      description: 'Advanced laser systems can safely and effectively treat a range of skin tones.'
    },
    {
      title: 'Quick Treatment Sessions',
      description: 'Most sessions are quick, with small areas taking only minutes to treat.'
    },
    {
      title: 'Smooth, Even Skin',
      description: 'Results in smoother, more even skin texture without the stubble or shadow of shaved hair.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Laser Hair Removal in Forest Hills, NY | Century Aesthetics</title>
        <meta name="description" content="Painless laser hair removal for all skin types with Motus AZ+ technology. FDA-approved, permanent hair reduction in Forest Hills, NY. Book your consultation today!" />
        <meta name="keywords" content="laser hair removal, hair removal forest hills, painless laser hair removal, motus laser, permanent hair reduction, laser hair removal near me" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/laser-hair-removal" />
        
        <meta property="og:title" content="Laser Hair Removal | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Professional laser hair removal for smooth, hair-free skin in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/laser-hair-removal" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">
     
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/laserhair.png" 
                  alt="Laser Hair Removal Treatment" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                PROFESSIONAL LASER HAIR REMOVAL FOR SILKY SMOOTH SKIN
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Laser Hair Removal is a non-invasive, state-of-the-art method for achieving long-term hair reduction and smoother skin. Using highly concentrated light beams, the procedure targets and destroys hair follicles at their root, preventing regrowth without harming the surrounding skin. This technology works best on individuals with dark hair and lighter skin tones, but advancements have made it increasingly effective for various skin types and tones. The procedure is quick, typically taking only 15-30 minutes per session for small areas such as the upper lip or underarms, while larger areas like the legs or back may require 30-60 minutes.
                </p>
                <p>
                  Unlike traditional hair removal methods like waxing or shaving, Laser Hair Removal offers long-lasting results with minimal discomfort. While multiple sessions are usually required to achieve optimal results—typically 6-8 treatments spaced several weeks apart—the outcomes are significantly more permanent than alternative options. After completing your treatment series, you may enjoy months or even years of smooth, hair-free skin. Additionally, laser hair removal reduces ingrown hairs and skin irritation often associated with shaving. To explore Laser Hair Removal and see how it can transform your skincare routine, contact Century Medical Services in Forest Hills, NY today.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Laser Hair Removal Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-5xl mx-auto bg-white rounded-lg p-8 shadow-2xl">
            <p className="text-center text-sm text-gray-600 mb-6">The cutting-edge technology of the Motus AZ+ represents the pinnacle in effective, fast and PAINLESS laser hair removal for all skin types delivering exceptional results every time.</p>
            
            {/* Pricing Tables */}
            <div className="overflow-x-auto">
              {/* Face Table */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-olive-darkest text-white">
                    <th className="px-4 py-3 text-left">FACE</th>
                    <th className="px-4 py-3 text-center">Price</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b"><td className="px-4 py-3">Forehead/Hairline</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Sideburns</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Ears</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Upper Lip</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Chin</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Neck/Neckline</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3 font-semibold">Full Face & Neck</td><td className="px-4 py-3 text-center font-semibold">$150</td></tr>
                </tbody>
              </table>

              {/* Upper Body Table */}
              <table className="w-full text-sm mt-6">
                <thead>
                  <tr className="bg-olive-darkest text-white">
                    <th className="px-4 py-3 text-left">UPPER BODY</th>
                    <th className="px-4 py-3 text-center">Price</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b"><td className="px-4 py-3">Underarms</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Areolas</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Belly Line</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Hands & Fingers</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Full Arms</td><td className="px-4 py-3 text-center">$130</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Chest + Abdomen</td><td className="px-4 py-3 text-center">$150</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Back</td><td className="px-4 py-3 text-center">$150</td></tr>
                  <tr className="border-b"><td className="px-4 py-3 font-semibold">Full Upper Body</td><td className="px-4 py-3 text-center font-semibold">$299</td></tr>
                  <tr className="border-b"><td className="px-4 py-3 font-semibold">Full Upper Body x 6</td><td className="px-4 py-3 text-center font-semibold">$1700</td></tr>
                </tbody>
              </table>

              {/* Lower Body Table */}
              <table className="w-full text-sm mt-6">
                <thead>
                  <tr className="bg-olive-darkest text-white">
                    <th className="px-4 py-3 text-left">LOWER BODY</th>
                    <th className="px-4 py-3 text-center">Price</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b"><td className="px-4 py-3">Half Legs</td><td className="px-4 py-3 text-center">$150</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Full Legs</td><td className="px-4 py-3 text-center">$300</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Brazilian</td><td className="px-4 py-3 text-center">$100</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Manzilian</td><td className="px-4 py-3 text-center">$100</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Buttocks</td><td className="px-4 py-3 text-center">$100</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Feet & Toe</td><td className="px-4 py-3 text-center">$50</td></tr>
                  <tr className="border-b"><td className="px-4 py-3 font-semibold">Full Lower Body</td><td className="px-4 py-3 text-center font-semibold">$299</td></tr>
                  <tr className="border-b"><td className="px-4 py-3 font-semibold">Full Lower Body x 6</td><td className="px-4 py-3 text-center font-semibold">$1700</td></tr>
                </tbody>
              </table>

              {/* Full Body Table */}
              <table className="w-full text-sm mt-6">
                <thead>
                  <tr className="bg-olive-darkest text-white">
                    <th className="px-4 py-3 text-left">FULL BODY</th>
                    <th className="px-4 py-3 text-center">Price</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b"><td className="px-4 py-3">Male 1 Session</td><td className="px-4 py-3 text-center">$350</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Male 6 Sessions</td><td className="px-4 py-3 text-center">$2100</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Male Unlimited 1 Year</td><td className="px-4 py-3 text-center">$2500</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Female 1 Session</td><td className="px-4 py-3 text-center">$350</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Female 6 Sessions</td><td className="px-4 py-3 text-center">$2100</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Female Unlimited 1 Year</td><td className="px-4 py-3 text-center">$2500</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Laser Hair Removal</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Say goodbye to unwanted hair with professional laser hair removal. Experience smooth, hair-free skin that lasts!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about laser hair removal treatments, what to expect during your sessions, and how many treatments you'll need.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('laser-hair-removal');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default LaserHairRemoval;